import React, { useState, useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Form from './Form'
import { SCHEMAS } from '../../class/constants'

import { Loader } from './../default'
import { acGetCurrentPlanTable, acGetMarketplaces, acGetPlanTables, acGetProductTables, acPostCustomer } from '../../class/actions'

function RegisterCustomerForm({
  onSubmit,
  errorCustomerPost, loadingCustomerPost,
  productTables, loadingProductTables,
  planTables, currentPlanTable, loadingPlanTables,
  marketplaces, loadingMarketplaces
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const _t = s => t(`forms.customerRegister.${s}`)
  const [plans, setPlans] = useState(null)

  useEffect(() => {
    if (!marketplaces.length && !loadingMarketplaces)
      dispatch(acGetMarketplaces())
  }, [dispatch, marketplaces, loadingMarketplaces])

  useEffect(() => {
    if (!planTables.length && !loadingPlanTables) {
      dispatch(acGetPlanTables())
      dispatch(acGetCurrentPlanTable())
    }
  }, [dispatch, planTables, loadingPlanTables])
  
  useEffect(() => {
    if (!productTables.length && !loadingProductTables) {
      dispatch(acGetProductTables())
    }
  }, [dispatch, productTables, loadingProductTables])

  const handleFormChanges = values => {
    if (!plans && currentPlanTable) {
      setPlans({
        plan_table_id: currentPlanTable.plan_table_id,
        items: currentPlanTable.plans.map(p => ({ label: p.name, value: p.plan_id }))
      })
    } else if(values.plan_table_id && plans.plan_table_id !== values.plan_table_id) {
      const selectedPlanTable = planTables.filter(p => p.plan_table_id === values.plan_table_id)

      if (selectedPlanTable && selectedPlanTable[0]) {
        setPlans({
          plan_table_id: values.plan_table_id,
          items: selectedPlanTable[0].plans.map(p => ({ label: p.name, value: p.plan_id }))
        })
      }
    }
  }

  const handleSubmit = values => {    
    const first_name = values.name.split(' ').slice(0, -1).join(' ');
    const last_name = values.name.split(' ').slice(-1).join(' ');

    return dispatch(acPostCustomer({
      cnpj: values.cnpj,
      cpf: values.cpf,
      first_name, last_name,
      email: values.email,
      phone_1: values.phone_1,
      sponsored_credit: (values.include_sponsored_credit && values.sponsored_credit) ? values.sponsored_credit : undefined,
      plan_table_id: (values.include_plan_table && values.plan_table_id) ? values.plan_table_id : undefined,
      product_table_id: (values.include_product_table && values.product_table_id) ? values.product_table_id : undefined,
      marketplace_id: (values.include_marketplace && values.marketplace_id) ? values.marketplace_id : undefined,
      should_subscribe: (values.should_subscribe && values.subscription_plan_id) ? values.should_subscribe : undefined,
      subscription_plan_id: (values.should_subscribe && values.subscription_plan_id) ? values.subscription_plan_id : undefined,
    }))
      .then(() => onSubmit())
      .catch(err => console.error(err))
  }

  const getErrorMsg = () => {
    if (!errorCustomerPost) { return '' }

    if (errorCustomerPost.__errorKst && errorCustomerPost.message) {
      return errorCustomerPost.message
    }

    switch (errorCustomerPost.status) {
      case 400: return t('screens.registerScreen.errors.400');
      default: return t('screens.registerScreen.errors.common');
    }
  }

  return (
    <Form
      onSubmit={handleSubmit}
      onClose={onSubmit}
      initialValues={{
        cnpj: '',
        cpf: '',
        name: '',
        email: '',
        phone_1: '',
        include_sponsored_credit: false,
        sponsored_credit: 0,
        include_plan_table: false,
        plan_table_id: null,
        should_subscribe: false,
        subscription_plan_id: null,
        include_product_table: false,
        product_table_id: null,
        include_marketplace: false,
        marketplace_id: null
      }}
      schema={SCHEMAS.CUSTOMER}
      btnText={loadingCustomerPost ? <Loader /> : _t('btn')}
      showCloseBtn
      errorMsg={getErrorMsg()}
      onUpdateValues={values => handleFormChanges(values)}
      fields={[
        { type: 'text', id: 'email', label: _t('email') },
        { type: 'cnpj', id: 'cnpj', label: _t('company') },
        { type: 'text', id: 'name', label: _t('name') },
        { type: 'cpf', id: 'cpf', label: _t('cpf'), },
        { type: 'phone', id: 'phone_1', label: _t('phone1') },
        { type: 'checkbox', id: 'include_sponsored_credit', label: _t('includeSponsoredCredit'), info: _t('tips.sponsoredCredit'), children: [
          { type: 'currency', id: 'sponsored_credit', label: _t('sponsoredCredit') }
        ] },
        { type: 'checkbox', id: 'include_plan_table', label: _t('includePlanTable'), info: _t('tips.planTable'), children: [
          { type: 'combobox', id: 'plan_table_id', label: _t('planTable'), options: planTables.map(p => ({ label: p.current_table ? `${p.name} (${_t('default')})` : p.name, value: p.plan_table_id })), isLoading: loadingPlanTables }
        ] },
        { type: 'checkbox', id: 'should_subscribe', label: _t('shouldSubscribe'), info: _t('tips.plan'), children: [
          { type: 'combobox', id: 'subscription_plan_id', label: _t('subscriptionPlan'), options: plans ? plans.items : [], isLoading: plans ? false : true }
        ] },
        { type: 'checkbox', id: 'include_product_table', label: _t('includeProductTable'), info: _t('tips.productTable'), children: [
          { type: 'combobox', id: 'product_table_id', label: _t('productTable'), options: productTables.map(p => ({ label: p.current_table ? `${p.name} (${_t('default')})` : p.name, value: p.product_table_id })), isLoading: loadingProductTables }
        ] },
        { type: 'checkbox', id: 'include_marketplace', label: _t('includeMarketplace'), info: _t('tips.marketplace'), children: [
          { type: 'combobox', id: 'marketplace_id', label: _t('marketplace'), options: marketplaces.map(p => ({ label: p.name, value: p.marketplace_id })), isLoading: loadingMarketplaces }
        ] }
      ]}
      splitFields={[
        ['cpf', 'phone_1'],
        ['cnpj', 'email']
      ]}
    />
  )
}

const mapStateToProps = ({ ReducerPlanTable, ReducerProductTable, ReducerPlan, ReducerMarketplace, ReducerRegister }) => ({
  planTables: ReducerPlanTable.planTables,
  currentPlanTable: ReducerPlanTable.currentTable, 
  loadingPlanTables: ReducerPlanTable.loadingPlanTables,

  productTables: ReducerProductTable.productTables,
  currentProductTable: ReducerProductTable.currentTable, 
  loadingProductTables: ReducerProductTable.loadingProductTables,

  plans: ReducerPlan.plans, 
  loadingPlans: ReducerPlan.loadingPlans,

  marketplaces: ReducerMarketplace.marketplaces, 
  loadingMarketplaces: ReducerMarketplace.loadingMarketplaces,

  errorCustomerPost: ReducerRegister.errorCustomerPost, 
  loadingCustomerPost: ReducerRegister.loadingCustomerPost,
})

export default connect(mapStateToProps)(RegisterCustomerForm);